<template>
  <div class="form-autocomplete-wrapper" :style="{ width }">
    <label v-if="label" class="form-autocomplete-label">{{ label }}</label>
    <input
      :value="modelValue"
      @input="onInput"
      @focus="showDropdown = true"
      @blur="onBlur"
      :placeholder="placeholder"
      :required="required"
      class="form-autocomplete-input"
      :style="inputStyle"
      @keydown.down.prevent="moveSelection(1)"
      @keydown.up.prevent="moveSelection(-1)"
      @keydown.enter.prevent="selectHighlighted"
    />
    <div class="form-autocomplete-dropdown" v-show="showDropdown && filteredOptions.length">
      <div
        v-for="(option, idx) in filteredOptions"
        :key="option.value || option"
        :class="['form-autocomplete-option', { highlighted: idx === highlightedIndex }]"
        @mousedown.prevent="selectOption(option)"
      >
        {{ option.label || option }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FormAutocomplete',
  props: {
    modelValue: String,
    label: String,
    placeholder: String,
    required: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: '100%',
    },
    options: {
      type: Array,
      default: () => [],
    },
    inputStyle: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showDropdown: false,
      search: this.modelValue || '',
      highlightedIndex: -1,
    };
  },
  computed: {
    filteredOptions() {
      const search = this.search.toLowerCase();
      return this.options.filter(opt => {
        const label = (opt.label || opt).toLowerCase();
        return label.includes(search);
      });
    },
  },
  watch: {
    modelValue(val) {
      this.search = val;
    },
  },
  methods: {
    onInput(e) {
      this.search = e.target.value;
      this.$emit('update:modelValue', this.search);
      this.showDropdown = true;
      this.highlightedIndex = -1;
    },
    selectOption(option) {
      const value = option.value || option;
      this.$emit('update:modelValue', value);
      this.search = value;
      this.showDropdown = false;
    },
    onBlur() {
      setTimeout(() => {
        this.showDropdown = false;
      }, 100);
    },
    moveSelection(dir) {
      if (!this.filteredOptions.length) return;
      let idx = this.highlightedIndex + dir;
      if (idx < 0) idx = this.filteredOptions.length - 1;
      if (idx >= this.filteredOptions.length) idx = 0;
      this.highlightedIndex = idx;
    },
    selectHighlighted() {
      if (this.highlightedIndex >= 0 && this.filteredOptions[this.highlightedIndex]) {
        this.selectOption(this.filteredOptions[this.highlightedIndex]);
      }
    },
  },
};
</script>

<style scoped>
.form-autocomplete-wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: 0;
}
.form-autocomplete-label {
  margin-bottom: 4px;
  font-weight: 600;
  font-size: 0.85rem;
  color: hsl(var(--foreground));
}
.form-autocomplete-input {
  padding: 8px 12px;
  border: 1px solid hsl(var(--input));
  border-radius: 8px;
  font-size: 0.9rem;
  outline: none;
  background: hsl(var(--background));
  color: hsl(var(--foreground));
  transition: border-color 0.2s, box-shadow 0.2s;
}
.form-autocomplete-input:focus {
  border-color: hsl(var(--ring));
  box-shadow: 0 0 0 2px hsl(var(--ring) / 0.25);
}
.form-autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  margin-top: 4px;
  background: hsl(var(--card));
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  box-shadow: 0 12px 24px hsl(var(--foreground) / 0.14);
  z-index: 20;
  max-height: 200px;
  overflow-y: auto;
}
.form-autocomplete-option {
  padding: 8px 12px;
  cursor: pointer;
  color: hsl(var(--foreground));
  font-size: 0.86rem;
  transition: background-color 0.15s, color 0.15s;
}
.form-autocomplete-option.highlighted,
.form-autocomplete-option:hover {
  background: hsl(var(--muted));
  color: hsl(var(--foreground));
}
</style> 
